import { lazy, Suspense, useEffect, useMemo } from 'react';
import './App.css';
import Layout from './Components/BodyComponent/Layout';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import AuthComponent from './Components/Authentication/AuthComponent';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Backdrop, CircularProgress } from '@mui/material';
import ScrollToTop from './Helpers/scroll-top';
import ServiceStatus from './Pages/ServiceStatus';
import { useSelector } from 'react-redux';
import OrgsCheck from './Pages/OrgsCheck/OrgsCheck';
import OrgInfo from './Pages/OrgInfo';
import UserInfo from './Pages/UserInfo';
import OverallStatistics from './Pages/OverallStatistics';
import FeatureFlagsPage from './Pages/FeatureFlagsPage';
import ExperimentsPage from './Pages/Experiments/ExperimentsPage';
import ExperimentDocsPage from './Pages/Experiments/ExperimentDocsPage';
import ExperimentRunPage from './Pages/Experiments/ExperimentRunPage';
import ExperimentRunsPage from './Pages/Experiments/ExperimentRunsPage';
import ExperimentEvaluationPage from './Pages/Experiments/ExperimentEvaluationPage';
import { Reports } from './Pages/Reports';
import SsoSignIn from './Components/Authentication/SsoSignIn';
import { OverallConfigurations } from './Pages/OverallConfigurations';
import * as Sentry from '@sentry/react';
import ExperimentStatusPage from './Pages/Experiments/ExperimentStatusPage';
import ExperimentResultsPage from './Pages/Experiments/ExperimentResultsPage';
import VersionsPage from './Pages/VersionsPage';
import { AlpsProjects } from './Pages/AlpsProjects';
import MaintenanceProjects from './Pages/MaintenanceProjects';

const SearchComponent = lazy(() => import('./Components/BodyComponent/SearchComponent'));
const Dashboard = lazy(() => import('./Components/BodyComponent/Dashboard'));
const OrgAdd = lazy(() => import('./Pages/OrgAdd'));
const FiledDocuments = lazy(() => import('./Pages/FiledDocuments'));
const NoPage = lazy(() => import('./Components/BodyComponent/NoPage'));

const roles = {
    console: 'console',
    evaluator: 'evaluator',
    admin: 'admin',
};

function ForbiddenPage({ basePagePath }) {
    useEffect(() => {
        toast.error('Access to the page is denied.');
    }, []);

    return <Navigate to={basePagePath} />;
}

function ProtectedRoute({ children }) {
    const { isAuthenticated, isConsole, isEvaluator } = useSelector((state) => state.auth);

    if (!isAuthenticated || !isConsole && !isEvaluator) {
        console.error({ isAuthenticated, isConsole, isEvaluator });
        return <Navigate to="/sso" />;
    }

    return children;
}

function AuthIsLoaded({ children }) {
    const authState = useSelector((state) => state.auth);

    if (authState.isLoading) {
        return (
            <Backdrop
                sx={{ backgroundColor: '#fff', color: '#4a78cb', zIndex: (theme) => theme.zIndex.drawer + 1 }}
                open={true}
            >
                <CircularProgress color="inherit" />
            </Backdrop>
        );
    }

    return children;
}

const baseLayoutPages = [
    // Console role
    {
        isIndex: true,
        path: null,
        element: <SearchComponent />,
        roles: [roles.console, roles.admin],
    },
    {
        path: '/alps-projects/',
        element: <AlpsProjects />,
        roles: [roles.console, roles.admin],
    },
    {
        path: '/maintenance-projects/',
        element: <MaintenanceProjects />,
        roles: [roles.console, roles.admin],
    },
    {
        path: '/app/',
        element: <SearchComponent />,
        roles: [roles.console, roles.admin],
    },
    {
        path: '/dashboard/',
        element: <Dashboard />,
        roles: [roles.console, roles.admin],
    },
    {
        path: '/service-status/',
        element: <ServiceStatus />,
        roles: [roles.console, roles.admin],
    },
    {
        path: '/filed-documents/',
        element: <FiledDocuments />,
        roles: [roles.console, roles.admin],
    },
    {
        path: '/check-organisations/',
        element: <OrgsCheck />,
        roles: [roles.console, roles.admin],
    },
    {
        path: '/overall-configurations/',
        element: <OverallConfigurations />,
        roles: [roles.console, roles.admin],
    },
    {
        path: '/overall-statistics/',
        element: <OverallStatistics />,
        roles: [roles.console, roles.admin],
    },
    {
        path: '/reports/',
        element: <Reports />,
        roles: [roles.console, roles.admin],
    },

    // Evaluator role
    {
        path: '/experiments/',
        element: <ExperimentsPage />,
        roles: [roles.evaluator, roles.admin],
    },
    {
        path: '/experiments/:experimentId/evaluation',
        element: <ExperimentEvaluationPage />,
        roles: [roles.evaluator, roles.admin],
    },
    {
        path: '/experiments/:experimentId/status',
        element: <ExperimentStatusPage />,
        roles: [roles.evaluator, roles.admin],
    },

    // No roles
    {
        path: '/add-organisation/',
        element: <OrgAdd />,
        roles: [roles.admin],
    },
    {
        path: '/search/organisations/:orgName/',
        element: <OrgInfo />,
        roles: [roles.console, roles.admin],
    },
    {
        path: '/search/users/:userEmail/ids/:userId/',
        element: <UserInfo />,
        roles: [roles.console, roles.admin],
    },
    {
        path: '/search/users/:userEmail/ids/:userId/folders/:folderId',
        element: <UserInfo />,
        roles: [roles.console, roles.admin],
    },
    {
        path: '/feature-flags/',
        element: <FeatureFlagsPage />,
        roles: [roles.console, roles.admin],
    },
    {
        path: '/experiments/:experimentId/docs',
        element: <ExperimentDocsPage />,
        roles: [roles.admin],
    },
    {
        path: '/experiments/:experimentId/results',
        element: <ExperimentResultsPage />,
        roles: [roles.admin],
    },
    {
        path: '/experiments/:experimentId/runs',
        element: <ExperimentRunsPage />,
        roles: [roles.admin],
    },
    {
        path: '/experiments/:experimentId/runs/:runId',
        element: <ExperimentRunPage />,
        roles: [roles.admin],
    },

    // Any role
    {
        path: '/versions/',
        element: <VersionsPage />,
        roles: [roles.console, roles.admin, roles.evaluator],
    },
];

function App() {
    const { isAdmin, isConsole, isEvaluator } = useSelector((state) => state.auth);
    const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);
    const userRoles = useMemo(() => {
        const userRoles = [];

        if (isAdmin) userRoles.push(roles.admin);
        if (isConsole) userRoles.push(roles.console);
        if (isEvaluator) userRoles.push(roles.evaluator);

        return userRoles;
    },
    [isConsole, isEvaluator, isAdmin]);

    const baseLayoutRotes = useMemo(() => {
        const basePage = baseLayoutPages.find((page) => page.roles.some((role) => userRoles.includes(role)));
        const basePagePath = basePage
            ? basePage.isIndex
                ? '/'
                : basePage.path
            : '/sso';

        return baseLayoutPages.map((page) => {
            const isAvailable = page.roles.some((role) => userRoles.includes(role));
            const element = isAvailable
                ? page.element
                : (<ForbiddenPage key={page.path ?? '/'} basePagePath={basePagePath} />);

            return {
                index: page.isIndex,
                path: page.path,
                element,
            };
        });
    }, [userRoles]);

    useEffect(() => {
        return () => {
            document.title = 'Avail Admin Panel';
        };
    }, []);

    return (
        <BrowserRouter>
            <AuthIsLoaded>
                <Suspense
                    fallback={(
                        <Backdrop
                            sx={{
                                backgroundColor: '#fff',
                                color: '#4a78cb',
                                zIndex: (theme) => theme.zIndex.drawer + 1,
                            }}
                            open={true}
                        >
                            <CircularProgress color="inherit" />
                        </Backdrop>
                    )}
                >
                    <ScrollToTop />
                    <SentryRoutes>
                        <Route path="/" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
                            {baseLayoutRotes.map(({ path, index, element }) => (
                                <Route
                                    key={path ?? 'index'}
                                    {...(index ? { index: true } : { path })}
                                    element={element}
                                />
                            ))}
                            <Route path="*" element={<NoPage />} />
                        </Route>
                        <Route
                            path="/sso"
                            element={<AuthComponent childComponent={<SsoSignIn />} />}
                        />
                    </SentryRoutes>
                    <ToastContainer
                        position="bottom-left"
                        autoClose={5000}
                        hideProgressBar={false}
                        newestOnTop={false}
                        closeOnClick
                        rtl={false}
                        pauseOnFocusLoss
                        draggable
                        pauseOnHover
                        style={{ width: '350px' }}
                    />
                </Suspense>
            </AuthIsLoaded>
        </BrowserRouter>
    );
}

export default App;
